import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  AlertTriangle, Clock, Cloud, Smartphone, Check, X,
  ChevronRight, RefreshCw
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * ConflictResolutionDialog - UI for resolving offline sync conflicts
 * 
 * When local changes conflict with server data, this dialog helps
 * users decide which version to keep.
 * 
 * Strategies:
 * - Keep Local: Use the offline changes
 * - Keep Server: Discard local changes, use server version
 * - Merge: Attempt to combine both versions (when possible)
 */

const ConflictResolutionDialog = ({ 
  open, 
  onOpenChange,
  conflicts = [],
  onResolve,
  onResolveAll
}) => {
  const [selectedStrategy, setSelectedStrategy] = useState('server_wins');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [resolving, setResolving] = useState(false);
  
  const currentConflict = conflicts[currentIndex];
  const hasMoreConflicts = currentIndex < conflicts.length - 1;
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleString();
  };
  
  const getFieldDiff = (local, server) => {
    if (!local || !server) return [];
    
    const diffs = [];
    const allKeys = new Set([...Object.keys(local), ...Object.keys(server)]);
    
    allKeys.forEach(key => {
      // Skip internal fields
      if (key.startsWith('_') || key === 'id' || key === 'syncStatus') return;
      
      const localVal = local[key];
      const serverVal = server[key];
      
      if (JSON.stringify(localVal) !== JSON.stringify(serverVal)) {
        diffs.push({
          field: key,
          local: localVal,
          server: serverVal
        });
      }
    });
    
    return diffs;
  };
  
  const handleResolve = async (strategy) => {
    setResolving(true);
    
    try {
      await onResolve(currentConflict, strategy);
      
      if (hasMoreConflicts) {
        setCurrentIndex(prev => prev + 1);
      } else {
        onOpenChange(false);
        setCurrentIndex(0);
      }
    } catch (error) {
      console.error('Failed to resolve conflict:', error);
    } finally {
      setResolving(false);
    }
  };
  
  const handleResolveAll = async () => {
    setResolving(true);
    
    try {
      await onResolveAll(selectedStrategy);
      onOpenChange(false);
      setCurrentIndex(0);
    } catch (error) {
      console.error('Failed to resolve all conflicts:', error);
    } finally {
      setResolving(false);
    }
  };

  if (!currentConflict) return null;

  const diffs = getFieldDiff(currentConflict.local, currentConflict.server);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="h-5 w-5" />
            Sync Conflict Detected
          </DialogTitle>
          <DialogDescription>
            {conflicts.length > 1 ? (
              <>Conflict {currentIndex + 1} of {conflicts.length} - Your local changes conflict with the server version.</>
            ) : (
              <>Your local changes conflict with the server version. Choose which to keep.</>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Item identifier */}
          <div className="text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-lg">
            <span className="font-medium">Item:</span> {currentConflict.local?.name || currentConflict.local?.title || currentConflict.id || 'Unknown'}
          </div>
          
          {/* Version comparison */}
          <div className="grid grid-cols-2 gap-3">
            {/* Local version */}
            <Card 
              className={cn(
                "cursor-pointer transition-all border-2",
                selectedStrategy === 'client_wins' 
                  ? "border-blue-500 bg-blue-50" 
                  : "border-slate-200 hover:border-slate-300"
              )}
              onClick={() => setSelectedStrategy('client_wins')}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <Smartphone className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">Your Changes</p>
                    <p className="text-xs text-slate-500">Local version</p>
                  </div>
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDate(currentConflict.local?._localUpdatedAt || currentConflict.local?.updatedAt)}
                </div>
                {selectedStrategy === 'client_wins' && (
                  <div className="mt-2 flex items-center gap-1 text-blue-600 text-xs font-medium">
                    <Check className="h-3 w-3" />
                    Selected
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Server version */}
            <Card 
              className={cn(
                "cursor-pointer transition-all border-2",
                selectedStrategy === 'server_wins' 
                  ? "border-emerald-500 bg-emerald-50" 
                  : "border-slate-200 hover:border-slate-300"
              )}
              onClick={() => setSelectedStrategy('server_wins')}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Cloud className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">Server Version</p>
                    <p className="text-xs text-slate-500">Latest saved</p>
                  </div>
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDate(currentConflict.server?.updated_at || currentConflict.server?.updatedAt)}
                </div>
                {selectedStrategy === 'server_wins' && (
                  <div className="mt-2 flex items-center gap-1 text-emerald-600 text-xs font-medium">
                    <Check className="h-3 w-3" />
                    Selected
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Field differences */}
          {diffs.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">Changes:</p>
              <div className="bg-slate-50 rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                {diffs.map((diff, idx) => (
                  <div key={idx} className="text-xs">
                    <span className="font-medium text-slate-600 capitalize">
                      {diff.field.replace(/_/g, ' ')}:
                    </span>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded truncate max-w-[45%]">
                        {String(diff.local || '(empty)')}
                      </span>
                      <ChevronRight className="h-3 w-3 text-slate-400 flex-shrink-0" />
                      <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded truncate max-w-[45%]">
                        {String(diff.server || '(empty)')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          {conflicts.length > 1 && (
            <Button
              variant="outline"
              onClick={handleResolveAll}
              disabled={resolving}
              className="w-full sm:w-auto"
            >
              {resolving ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Apply to All ({conflicts.length})
            </Button>
          )}
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={resolving}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleResolve(selectedStrategy)}
              disabled={resolving}
              className={cn(
                selectedStrategy === 'client_wins' 
                  ? "bg-blue-600 hover:bg-blue-700" 
                  : "bg-emerald-600 hover:bg-emerald-700"
              )}
            >
              {resolving ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              {selectedStrategy === 'client_wins' ? 'Keep My Changes' : 'Use Server Version'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConflictResolutionDialog;
